// createPlayerControl.js
import attachLandscapeFullscreen from '../resource/landscapeFullscreen.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

/** Video.js-shaped adapter over createPlayer. Keeps listeners alive across video element changes. */
const createPlayerControl = ({
  getActivePlayer,
  getContainerElement,
  getSource,
  getCallbacks = NULL_FUNCTION,
  getIsSplashActive = () => false,
}) => {
  const externalListeners = new Map(); // eventName -> Set<handler>
  let attachedVideoElement = null;
  let pendingPlaybackRate = null;
  let pendingLoop = null;
  let landscapeUnsubscribe = NULL_FUNCTION;

  const getActiveVideoElement = () =>
    getActivePlayer()?.getVideoElement?.() ?? null;

  const isVideoElementPlaying = element =>
    Boolean(element && element.paused === false && !element.ended);

  const readDuration = player => {
    const liveDuration = player?.getVideoElement?.()?.duration;
    if (Number.isFinite(liveDuration) && liveDuration > 0) return liveDuration;
    return player?.getSnapshot?.()?.duration ?? 0;
  };

  const syncListeners = () => {
    const nextElement = getActiveVideoElement();
    if (attachedVideoElement === nextElement) return;
    if (attachedVideoElement) {
      for (const [eventName, handlers] of externalListeners) {
        for (const handler of handlers) {
          attachedVideoElement.removeEventListener(eventName, handler);
        }
      }
    }
    if (nextElement) {
      for (const [eventName, handlers] of externalListeners) {
        for (const handler of handlers) {
          nextElement.addEventListener(eventName, handler);
        }
      }
      if (pendingPlaybackRate !== null) {
        nextElement.playbackRate = pendingPlaybackRate;
      }
      if (pendingLoop !== null) {
        nextElement.loop = pendingLoop;
      }
    }
    attachedVideoElement = nextElement;
  };

  const control = {
    play: () => getActivePlayer()?.play() ?? Promise.resolve(),
    pause: () => {
      getActivePlayer()?.pause();
    },
    paused: () => !getActivePlayer()?.getSnapshot()?.isPlaying,
    // The adapter's seek API does not emit a synchronous 'seeking' event before
    // the async seek completes, so the control synthesises onSeeking and
    // onProgressUpdate here to keep SeekBar / progress indicators responsive.
    currentTime: value => {
      const player = getActivePlayer();
      if (value !== undefined) {
        getCallbacks()?.onSeeking?.({ type: 'seeking' });
        player?.seek(value);
        getCallbacks()?.onProgressUpdate?.({
          currentTime: value,
          duration: readDuration(player),
        });
        return value;
      }
      return player?.getSnapshot()?.currentTime ?? 0;
    },
    muted: value => {
      const player = getActivePlayer();
      if (value !== undefined) {
        player?.setMuted(value);
        return value;
      }
      return player?.isMuted() ?? false;
    },
    duration: () => readDuration(getActivePlayer()),
    playbackRate: value => {
      if (value !== undefined) {
        pendingPlaybackRate = value;
        let element = getActiveVideoElement();
        if (!element) {
          syncListeners();
          element = getActiveVideoElement();
        }
        if (element) {
          element.playbackRate = value;
        }
        return value;
      }
      return getActiveVideoElement()?.playbackRate ?? pendingPlaybackRate ?? 1;
    },
    bufferedEnd: () => {
      const buffered = getActiveVideoElement()?.buffered;
      if (!buffered || buffered.length === 0) return 0;
      return buffered.end(buffered.length - 1);
    },
    poster: value => {
      const element = getActiveVideoElement();
      if (value !== undefined && element) element.poster = value;
      return element?.poster ?? '';
    },
    loop: value => {
      if (value !== undefined) {
        pendingLoop = value;
        const element = getActiveVideoElement();
        if (element) {
          element.loop = value;
        }
        return value;
      }
      return getActiveVideoElement()?.loop ?? pendingLoop ?? false;
    },
    el: () => getContainerElement(),
    currentSrc: () => {
      const { source } = getSource() ?? {};
      return source?.dash || source?.hls || source?.mp4 || '';
    },
    getAssetUri: () => {
      const { source, splashVideo } = getSource() ?? {};
      return (
        source?.dash ||
        source?.hls ||
        source?.mp4 ||
        splashVideo?.dash ||
        splashVideo?.hls ||
        splashVideo?.mp4 ||
        ''
      );
    },
    isSplashActive: () => getIsSplashActive(),
    requestFullscreen: () => getContainerElement()?.requestFullscreen?.(),
    exitFullscreen: () => document.exitFullscreen?.(),
    isFullscreen: () =>
      typeof document !== 'undefined' &&
      document.fullscreenElement === getContainerElement(),
    landscapeFullscreen: options => {
      landscapeUnsubscribe();
      landscapeUnsubscribe = attachLandscapeFullscreen({
        player: control,
        options,
      });
    },
    on: (eventName, handler) => {
      if (!eventName || typeof handler !== 'function') return;
      let handlers = externalListeners.get(eventName);
      if (!handlers) {
        handlers = new Set();
        externalListeners.set(eventName, handlers);
      }
      if (handlers.has(handler)) return;
      handlers.add(handler);
      if (attachedVideoElement) {
        attachedVideoElement.addEventListener(eventName, handler);
      } else {
        syncListeners();
      }
      // Handler attached after the native 'playing' event already fired.
      if (
        eventName === 'playing' &&
        attachedVideoElement &&
        (isVideoElementPlaying(attachedVideoElement) ||
          getActivePlayer()?.getSnapshot?.()?.isPlaying)
      ) {
        handler({
          type: eventName,
          target: attachedVideoElement,
          currentTarget: attachedVideoElement,
        });
      }
    },
    off: (eventName, handler) => {
      const handlers = externalListeners.get(eventName);
      if (!handlers || !handlers.has(handler)) return;
      handlers.delete(handler);
      if (handlers.size === 0) externalListeners.delete(eventName);
      attachedVideoElement?.removeEventListener(eventName, handler);
    },
  };

  const dispose = () => {
    landscapeUnsubscribe();
    landscapeUnsubscribe = NULL_FUNCTION;
    if (attachedVideoElement) {
      for (const [eventName, handlers] of externalListeners) {
        for (const handler of handlers) {
          attachedVideoElement.removeEventListener(eventName, handler);
        }
      }
    }
    externalListeners.clear();
    attachedVideoElement = null;
  };

  return { control, syncListeners, dispose };
};

export default createPlayerControl;
